import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
// @mui
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
//
import axios, { endpoints } from 'src/utils/axios';
import { useAuthContext } from 'src/auth/hooks';
import { RouterLink } from 'src/routes/components';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import { IftaFiling } from 'src/models';

// ----------------------------------------------------------------------

const navSx = (active: boolean) => ({
  px: 1.5,
  color: active ? 'text.primary' : 'text.secondary',
  fontWeight: active ? 700 : 500,
  bgcolor: active ? '#F0F3F0' : 'transparent',
  '&:hover': { bgcolor: '#F0F3F0' },
});

export default function AppLayout() {
  const { session, logout } = useAuthContext();
  const router = useRouter();
  const location = useLocation();
  const [checkingCurrent, setCheckingCurrent] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleLogout = async () => {
    await logout();
    router.replace(paths.auth.login);
  };

  // "Current Filing" only changes the route when an unsubmitted filing exists;
  // with none, the offer to start one pops right here over the current page.
  const openCurrentFiling = async () => {
    setCheckingCurrent(true);
    try {
      const response = await axios.get<IftaFiling[]>(endpoints.filings.findAll);
      const current = (response.data || []).find((filing) => filing.paymentStatus !== 'paid');
      if (current) {
        router.push(paths.filings.wizard(current.id));
        return;
      }
      setAskOpen(true);
    } catch (error) {
      enqueueSnackbar(
        error?.response
          ? 'Could not load your filings. Please try again.'
          : 'Please check your internet connection and try again.',
        { variant: 'error' }
      );
    } finally {
      setCheckingCurrent(false);
    }
  };

  const startFiling = async () => {
    setCreating(true);
    try {
      const response = await axios.post<IftaFiling>(endpoints.filings.create, {
        title: 'IFTA Filing',
      });
      setAskOpen(false);
      router.push(paths.filings.wizard(response.data.id));
    } catch (error) {
      const message = error?.response?.data?.[0]?.message;
      enqueueSnackbar(message || 'Could not start a filing', { variant: message ? 'warning' : 'error' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F7F9F7' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ bgcolor: '#FFFFFF', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        <Toolbar sx={{ minHeight: { xs: 88 }, py: 1.5, gap: 3 }}>
          <Box
            component={RouterLink}
            href={paths.filings.root}
            sx={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}
          >
            <Box
              component="img"
              src="/logo/logo_ifta.png"
              alt="Counting Trucks IFTA"
              sx={{ width: 150, height: 'auto', display: 'block' }}
            />
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
            <Button
              component={RouterLink}
              href={paths.filings.root}
              size="small"
              data-testid="nav-ifta-filings"
              sx={navSx(location.pathname === paths.filings.root)}
            >
              IFTA Filings
            </Button>
            <Button
              size="small"
              onClick={openCurrentFiling}
              disabled={checkingCurrent}
              data-testid="nav-current-filing"
              sx={navSx(location.pathname.startsWith(paths.filings.current))}
            >
              Current Filing
            </Button>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
            >
              {session?.user?.email}
            </Typography>
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
        <Outlet />
      </Container>

      <Dialog
        open={askOpen}
        onClose={() => setAskOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="no-current-filing"
      >
        <DialogTitle>There are no filings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Would you like to start a new one? We&apos;ll create the filing and take you straight
            into the flow.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setAskOpen(false)} data-testid="start-current-filing-cancel">
            Not now
          </Button>
          <LoadingButton
            variant="contained"
            color="success"
            loading={creating}
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={startFiling}
            data-testid="start-current-filing"
          >
            Start new filing
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
